import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectSettings } from '../../../store/selectors/databaseSelectors';
import { updateSetting } from '../../../store/slices/databaseSlice';
import { settingsService } from '../../../services';
import { TOAST_CONFIG, SETTING_TYPES, SETTING_LABELS, SETTING_CONSTRAINTS } from '../../../constants';
import toast from 'react-hot-toast';
import { Setting } from '../../../types';

interface SettingsManagementProps {
  showOnlyGeneral?: boolean;
}

export const SettingsManagement: React.FC<SettingsManagementProps> = ({ showOnlyGeneral = false }) => {
  const dispatch = useAppDispatch();
  const allSettings = useAppSelector(selectSettings);
  const settings = allSettings.filter(s => s.type !== SETTING_TYPES.CLIENT_LOGOS);

  const [editingSetting, setEditingSetting] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatValue = (value: unknown): string => {
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const parseValue = (input: string): unknown => {
    const trimmed = input.trim();
    const num = Number(trimmed);
    if (!isNaN(num) && trimmed !== '') return num;
    try {
      return JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  };

  const handleEdit = (setting: Setting) => {
    setEditingSetting(setting.id);
    setEditValue(formatValue(setting.value));
  };

  const handleCancel = () => {
    setEditingSetting(null);
    setEditValue('');
  };

  const handleSave = async (setting: Setting) => {
    if (!editValue.trim()) {
      toast.error('Value cannot be empty', TOAST_CONFIG.toastOptions.error);
      return;
    }

    const parsedValue = parseValue(editValue);

    const constraints = SETTING_CONSTRAINTS[setting.type as keyof typeof SETTING_CONSTRAINTS];
    if (constraints && typeof parsedValue === 'number') {
      if (constraints.min !== undefined && parsedValue < constraints.min) {
        const label = SETTING_LABELS[setting.type]?.label || setting.type;
        toast.error(`${label} must be at least ${constraints.min}`, TOAST_CONFIG.toastOptions.error);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const updatedSetting = await settingsService.update(setting.id, { value: parsedValue });
      dispatch(updateSetting(updatedSetting));
      toast.success('Setting updated successfully!', TOAST_CONFIG.toastOptions.success);
      setEditingSetting(null);
      setEditValue('');
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error('Failed to update setting. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSettingInfo = (type: string) => {
    return SETTING_LABELS[type] || { label: type, description: '' };
  };

  return (
    <div className="p-4">
      <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-900">
            {showOnlyGeneral ? 'General Settings' : 'Application Settings'}
          </h3>
          <span className="text-xs text-slate-500">
            {settings.length} setting{settings.length !== 1 ? 's' : ''}
          </span>
        </div>

        {settings.length > 0 ? (
          <div className="space-y-3">
            {settings.map((setting) => {
              const info = getSettingInfo(setting.type);
              const isEditing = editingSetting === setting.id;

              return (
                <div
                  key={setting.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">
                        {info.label}
                      </span>
                      <span className="text-xs text-slate-400 font-mono">
                        ({setting.type})
                      </span>
                    </div>
                    {info.description && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {info.description}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-20 px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                        <button
                          onClick={() => handleSave(setting)}
                          disabled={isSubmitting}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                          title="Save"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={handleCancel}
                          disabled={isSubmitting}
                          className="p-1.5 text-slate-500 hover:bg-slate-100 rounded transition-colors disabled:opacity-50"
                          title="Cancel"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="px-3 py-1 bg-amber-100 text-amber-800 font-semibold text-sm rounded-full">
                          {formatValue(setting.value)}
                        </span>
                        <button
                          onClick={() => handleEdit(setting)}
                          className="p-1.5 text-slate-500 hover:bg-slate-100 rounded transition-colors"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-slate-400 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-slate-500 text-sm">No settings configured</p>
            <p className="text-slate-400 text-xs mt-1">Run database seed to initialize default settings</p>
          </div>
        )}
      </div>
    </div>
  );
};
